import admin from "@/middlewares/admin";
import updateCompanyInStore from "@/middlewares/updateCompanyInStore";
import auth from "@/middlewares/auth";
import guest from "@/middlewares/guest";
import tokenExpiry from "@/middlewares/tokenExpiry";
import onboarded from "@/middlewares/onboarded";
import redirectToDashboard from "@/middlewares/redirectToDashboard";
import subscribed from "@/middlewares/subscribed";
import verify from "@/middlewares/verify";
import storeRegistrationType from "@/middlewares/storeRegistrationType";
import redirectToRegistrationCompleted from "@/middlewares/redirectToRegistrationCompleted";
import redirectToPasswordReset from "@/middlewares/redirectToPasswordReset";

const defaultMiddlewares = [
    // order is important
    redirectToPasswordReset,
    auth,
    verify,
];

const list = [
    {
        path: "/",
        name: 'home',
        redirect: { name: "onboarding" },
        meta: {
            middleware: [
                ...defaultMiddlewares,
                redirectToDashboard
            ]
        }
    },

    {
        path: "/onboarding",
        name: "onboarding",
        component: () => import("@/views/pages/auth/Verified.vue"),
        meta: {
            middleware: [
                ...defaultMiddlewares,
                redirectToRegistrationCompleted,
                redirectToDashboard
            ]
        },
    },

    {
        path: "/onboarding/choose-plan",
        name: "onboarding.choose-plan",
        component: () => import("@/views/pages/SecretaryService.vue"),
        meta: {
            pageTitle: "Company Register",
            middleware: [
                ...defaultMiddlewares,
                redirectToDashboard
            ]
        },
    },

    {
        path: "/onboarding/company/register",
        name: "onboarding.company.register",
        component: () => import("@/views/pages/OnBoarding.vue"),
        meta: {
            pageTitle: "companyInfo",
            middleware: [
                ...defaultMiddlewares,
                redirectToDashboard
            ]
        },
    },

    {
        path: "/onboarding/company/register/completed",
        name: "onboarding.company.register.completed",
        component: () => import("@/views/pages/Completed.vue"),
        meta: {
            pageTitle: "companyFormCompleted",
            middleware: [
                ...defaultMiddlewares,
                subscribed,
                redirectToDashboard
            ]
        },
    },

    {
        path: "/onboarding/company/send-documents",
        name: "onboarding.company.sendDocuments",
        component: () => import("@/views/pages/SendDocuments.vue"),
        meta: {
            pageTitle: "Send Email",
            middleware: [
                ...defaultMiddlewares,
                subscribed,
                redirectToDashboard
            ]
        },
    },

    // transfer a company
    {
        path: "/onboarding/transfer-company/register",
        name: "onboarding.transfer-company.register",
        component: () => import("@/views/pages/OnBoarding.vue"),
        meta: {
            pageTitle: "companyInfo",
            middleware: [
                ...defaultMiddlewares,
                redirectToDashboard
            ]
        },
    },

    {
        path: "/onboarding/transfer-company/register/completed",
        name: "onboarding.transfer-company.register.completed",
        component: () => import("@/views/pages/Completed.vue"),
        meta: {
            pageTitle: "companyFormCompleted",
            middleware: [
                ...defaultMiddlewares,
                subscribed,
                redirectToDashboard
            ]
        },
    },

    {
        path: "/onboarding/transfer-company/send-documents",
        name: "onboarding.transfer-company.sendDocuments",
        component: () => import("@/views/pages/SendDocuments.vue"),
        meta: {
            pageTitle: "Send Email",
            middleware: [
                ...defaultMiddlewares,
                subscribed,
                redirectToDashboard
            ]
        },
    },
    // end transfer a company

    {
        path: '/dashboard',
        name: 'company.dashboard',
        component: () => import ("@/views/components/Dashboard.vue"),
        meta: {
            middleware: [
                ...defaultMiddlewares,
                onboarded
            ]
        },
    },

    // profiles
    {
        path: '/profile',
        name: 'profile',
        component: () => import ("@/views/components/profile/Index.vue"),
        meta: {
            pageTitle: 'Profile',
            middleware: [
                ...defaultMiddlewares,
                onboarded
            ]
        },
    },

    {
        path: '/signature',
        name: 'signature',
        component: () => import ("@/views/components/profile/Index.vue"),
        meta: {
            pageTitle: 'Signature',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    // companies
    {
        path: '/companies',
        name: 'companies',
        component: () => import ("@/views/components/companies/company/Company.vue"),
        meta: {
            pageTitle: 'Companies',
            breadcrumb: [
                {
                    text: 'Companies',
                    to: {name: 'companies'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    // company children
    {
        path: '/company/brief-particular',
        name: 'brief-particulars',
        component: () => import ("@/views/components/companies/brief-particulars/BriefParticular.vue"),
        meta: {
            pageTitle: 'Brief Particular',
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    {
        path: '/company/share-capital',
        name: 'share-capitals',
        component: () => import ("@/views/components/companies/share-capitals/ShareCapital.vue"),
        meta: {
            pageTitle: 'Share Capital',
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    {
        path: '/company/ceo',
        name: 'ceo',
        component: () => import ("@/views/components/companies/chief-executive-officers/CEO.vue"),
        meta: {
            pageTitle: 'CEO',
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    {
        path: '/company/directors',
        name: 'directors',
        component: () => import ("@/views/components/companies/directors/Director.vue"),
        meta: {
            pageTitle: 'Director',
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    {
        path: '/company/nominee-directors',
        name: 'nominee-directors',
        component: () => import ("@/views/components/companies/nominee-directors/NomineeDirector.vue"),
        meta: {
            pageTitle: 'Nominee Director',
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    {
        path: '/company/nominee-shareholders',
        name: 'nominee-shareholders',
        component: () => import ("@/views/components/companies/nominee-shareholders/NomineeShareholder.vue"),
        meta: {
            pageTitle: 'Nominee Shareholder',
            middleware: [
                ...defaultMiddlewares,
                updateCompanyInStore
            ]
        },
    },

    {
        path: '/company/shareholders',
        name: 'shareholders',
        component: () => import ("@/views/components/companies/shareholders/Shareholder.vue"),
        meta: {
            pageTitle: 'Shareholder',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/secretaries',
        name: 'secretaries',
        component: () => import ("@/views/components/companies/secretaries/Secretary.vue"),
        meta: {
            pageTitle: 'Secretary',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/data-protection-officers',
        name: 'data-protection-officers',
        component: () => import ("@/views/components/companies/data-protection-officers/DPO.vue"),
        meta: {
            pageTitle: 'Data Protection Officer',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/auditors',
        name: 'auditors',
        component: () => import ("@/views/components/companies/auditors/Auditor.vue"),
        meta: {
            pageTitle: 'Auditor',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/share-certificates',
        name: 'share-certificates',
        component: () => import ("@/views/components/companies/share-certificates/ShareCertificate.vue"),
        meta: {
            pageTitle: 'Share Certificate',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    // documents
    {
        path: '/documents',
        name: 'documents',
        component: () => import ("@/views/components/documents/view-documents/Document.vue"),
        meta: {
            pageTitle: 'Charge',
            middleware: [
                ...defaultMiddlewares,
                admin,
            ]
        },
    },

    // signing
    {
        path: '/signing-list/pre-incorporation/documents',
        name: 'pre-incorporation',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Incorporation Documents',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/incorporation/documents',
        name: 'incorporation',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Incorporation Documents',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/secretary-appointment/documents',
        name: 'secretary-appointment',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Secretary Appointment Documents',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/change-of-fye/documents',
        name: 'change-of-fye',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Change of FYE Documents',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/director-fee/documents',
        name: 'director-fee',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Director Fee',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/change-director/documents',
        name: 'director-changes',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Director Changes',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/change-nominator/documents',
        name: 'nominator-changes-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Nominator Changes',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/update-of-particulars-documents/documents',
        name: 'update-of-particulars-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Update of Particulars',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/change-auditor/documents',
        name: 'auditor-changes',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Auditor Changes',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/secretaries-resignation/documents',
        name: 'secretaries-resignation-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Secretaries Resignation',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/data-protection-officers/documents',
        name: 'data-protection-officers-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Data Protection Officers',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/change-ro/documents',
        name: 'change-ro',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Change Registered Office Address',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/change-of-company-name/documents',
        name: 'change-of-company-name-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Change of Company Name',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/interim-dividend/documents',
        name: 'interim-dividend',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Interim Dividend',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/annual-return/documents',
        name: 'annual-return',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Annual Return Documents',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/agm/documents',
        name: 'agm',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'AGM',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/agm-dispensation/documents',
        name: 'agm-dispensation-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'AGM Dispensation',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/allotment/documents',
        name: 'allotment-documents',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Allotment',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/strike-off/documents',
        name: 'strike-off',
        component: () => import("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Business Activities',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/business-activities/documents',
        name: 'business-activities',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Business Activities',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/transfer/documents',
        name: 'transfer-documents',
        component: () => import("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Share Transfer',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/share-cancellation/documents',
        name: 'share-cancellation-documents',
        component: () => import("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Share Cancellation',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/share-buy-back/documents',
        name: 'share-buy-back-documents',
        component: () => import("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Share Buy Back',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/update-paid-up-capital/documents',
        name: 'update-paid-up-capital-documents',
        component: () => import("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Capital Payment',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/signing-list/sale-of-property/documents',
        name: 'sale-of-property',
        component: () => import ("@/views/components/signings/process-documents/WholeProcess.vue"),
        meta: {
            pageTitle: 'Sale of Property',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    // external documents
    {
        path: '/:process/prepare-external-documents/:document',
        name: 'prepare-external-documents',
        component: () => import ("@/views/components/external-documents/Document.vue"),
        meta: {
            layout: "full",
            pageTitle: 'External Document',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/send-gmail',
        name: 'send-gmail',
        component: () => import ("@/views/components/signings/process-documents/SendGmail.vue"),
        meta: {
            // layout: "full",
            pageTitle: 'Send Signature Invitation Mail',
            middleware: [
                ...defaultMiddlewares,
                admin,
            ]
        },
    },

    {
        path: '/signing-list/:process/documents/:document',
        name: 'sign.document',
        component: () => import("@/views/components/signings/SignDocument.vue"),
        meta: {
            layout: "full",
            middleware: [
                auth,
            ]
        },
    },
    {
        path: '/signing-list/documents/:document/view',
        name: 'sign.document.view',
        component: () => import("@/views/components/signings/PdfViewer.vue"),
        meta: {
            layout: "full",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    // registers
    {
        path: '/register/secretaries/documents',
        name: 'register-of-secretaries',
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: 'Register of Secretaries',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/ceo/documents",
        name: "register-of-ceo",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of CEO",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/register/directors/documents',
        name: 'register-of-directors',
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: 'Register of Directors',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/register/auditors/documents',
        name: 'register-of-auditors',
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: 'Register of Auditors',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/register/allotments/documents',
        name: 'register-of-allotments',
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: 'Register of Allotments',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: '/register/members/documents',
        name: 'register-of-members',
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: 'Register of Members',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/director-interests/documents",
        name: "register-of-director-interests",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of Director's Interest",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/nominee-directors/documents",
        name: "register-of-nominee-directors",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of Nominee Directors",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/nominee-shareholders/documents",
        name: "register-of-nominee-shareholders",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of Nominee Directors",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/controllers/documents",
        name: "register-of-controllers",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of Controllers",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/charges/documents",
        name: "register-of-charges",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of Charges",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    {
        path: "/register/transfers/documents",
        name: "register-of-transfers",
        component: () => import ("@/views/components/registers/WholeRegisterProcess.vue"),
        meta: {
            pageTitle: "Register of Transfers",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    // compliance
    {
        path: '/compliance/mas-list',
        name: 'compliance.mas-list',
        component: () => import ("@/views/components/compliance/mas-list/MasList.vue"),
        meta: {
            pageTitle: 'Lists of Designated Individuals and Entities',
            middleware: [
                ...defaultMiddlewares,
                admin,
            ]
        },
    },

    {
        path: '/compliance/mas-screening',
        name: 'compliance.mas-screening',
        component: () => import ("@/views/components/compliance/mas-screening/MasScreening.vue"),
        meta: {
            pageTitle: 'Mas Screening',
            middleware: [
                ...defaultMiddlewares,
                admin,
            ]
        },
    },

    {
        path: '/compliance/acra-list',
        name: 'compliance.acra-list',
        component: () => import ("@/views/components/compliance/acra-list/AcraList.vue"),
        meta: {
            pageTitle: 'ACRA list',
            middleware: [
                ...defaultMiddlewares,
                admin,
            ]
        },
    },

    {
        path: '/new-requirements/rons',
        name: 'new-requirements.rons',
        component: () => import ("@/views/components/new-requirements/RegisterOfNomineeShareholders.vue"),
        meta: {
            pageTitle: 'RONS',
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },
    
    {
        path: "/user-management",
        name: "userManagement",
        component: () => import("@/views/components/user-management/UserManagement.vue"),
        meta: {
            pageTitle: 'User Management',
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/terms-of-service',
        name: 'terms-of-service',
        component: () => import("@/views/pages/TermsOfService.vue"),
        meta: {
            layout: "full",
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },
    
    {
        path: '/company/charges',
        name: 'charges',
        component: () => import ("@/views/components/companies/charges/Charge.vue"),
        meta: {
            pageTitle: 'Charge',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/registrable-controllers',
        name: 'registrable-controllers',
        component: () => import ("@/views/components/companies/registrable-controllers/RegistrableController.vue"),
        meta: {
            pageTitle: 'Registrable Controller',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/beneficial-owners',
        name: 'beneficial-owners',
        component: () => import ("@/views/components/companies/beneficial-owners/BeneficialOwner.vue"),
        meta: {
            pageTitle: 'Beneficial Owner',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/director-interests',
        name: 'director-interests',
        component: () => import ("@/views/components/companies/director-interests/DirectorInterest.vue"),
        meta: {
            pageTitle: 'Director Interest',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/allotments',
        name: 'allotments',
        component: () => import ("@/views/components/companies/allotments/Allotment.vue"),
        meta: {
            pageTitle: 'Allotment',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/transfers',
        name: 'transfers',
        component: () => import ("@/views/components/companies/transfers/Transfer.vue"),
        meta: {
            pageTitle: 'Transfer',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/share-cancellation',
        name: 'share-cancellation',
        component: () => import ("@/views/components/companies/share-cancellation/ShareCancellation.vue"),
        meta: {
            pageTitle: 'Share Cancellation',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/company/share-buy-back',
        name: 'share-buy-back',
        component: () => import ("@/views/components/companies/share-buyback/ShareBuyback.vue"),
        meta: {
            pageTitle: 'Share Buy Back',
            middleware: [
                ...defaultMiddlewares,
            ]
        },
    },

    {
        path: '/prepare-documents',
        name: 'prepare-documents',
        component: () => import ("@/views/components/documents/prepare-documents/GenerateDocuments.vue"),
        meta: {
            pageTitle: 'Prepare Documents',
            middleware: [
                ...defaultMiddlewares,
                admin,
            ]
        },
    },

    // formation routes
    {
        path: '/formations',
        name: 'formations',
        component: () => import ("@/views/components/formations/Index.vue"),
        meta: {
            pageTitle: 'Formations',
            breadcrumb: [
                {
                    text: 'Formations',
                    to: {name: 'formations'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin,
                onboarded
            ]
        },
    },

    {
        path: '/formations/edit',
        name: 'formations.edit',
        component: () => import ("@/views/components/formations/Edit.vue"),
        meta: {
            pageTitle: 'Formations',
            navActiveLink: 'formations',
            breadcrumb: [
                {
                    text: 'Formations',
                    to: {name: 'company.formations'},
                },
                {
                    text: 'Edit',
                    active: true
                },
            ],

            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/formations/transfer-company/edit',
        name: 'formations.transfer-company.edit',
        component: () => import ("@/views/components/formations/TransferCompanyEdit.vue"),
        meta: {
            pageTitle: 'Transfer Company',
            // sharing the formations route also for company transfer edit
            navActiveLink: 'formations',
            breadcrumb: [
                {
                    text: 'Transfer Company',
                    to: {name: 'transfer-company.formations'},
                },
                {
                    text: 'Edit',
                    active: true
                },
            ],

            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    // setup
    {
        path: '/setup/share-type',
        name: 'share-type',
        component: () => import ("@/views/components/setup/share-type/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Share Type',
                    to: {name: 'setup.share-type'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/secretary-firm',
        name: 'secretary-firm',
        component: () => import ("@/views/components/setup/secretary-firm/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Secretary Firm',
                    to: {name: 'setup.secretary-firm'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/secretary',
        name: 'secretary',
        component: () => import ("@/views/components/setup/secretary/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Secretary',
                    to: {name: 'setup.secretary'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/operating-hours',
        name: 'operating-hours',
        component: () => import ("@/views/components/setup/operating-hours/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Operating Hours',
                    to: {name: 'setup.operating-hours'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/fee',
        name: 'fee',
        component: () => import ("@/views/components/setup/fee/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Fee',
                    to: {name: 'setup.fee'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/nominee-director',
        name: 'nominee-director',
        component: () => import ("@/views/components/setup/nominee-director/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Nominee Director',
                    to: {name: 'setup.nominee-director'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/auditor',
        name: 'auditor',
        component: () => import ("@/views/components/setup/auditor/Index.vue"),
        meta: {
            pageTitle: 'Setup',
            breadcrumb: [
                {
                    text: 'Auditor',
                    to: {name: 'setup.auditor'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    {
        path: '/setup/officer',
        name: 'officer',
        component: () => import ("@/views/components/setup/officer/Index.vue"),
        meta: {
            pageTitle: 'Officer',
            breadcrumb: [
                {
                    text: 'Officer',
                    to: {name: 'setup.officer'},
                    active: true
                },
            ],
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },

    // download center
    {
        path: '/download-center',
        name: 'download-center',
        component: () => import ("@/views/components/download-center/DownloadCenter.vue"),
        meta: {
            pageTitle: 'Download',
            middleware: [
                ...defaultMiddlewares,
                admin
            ]
        },
    },
    

    {
        path: "/login",
        name: "login",
        component: () => import("@/views/pages/auth/Login.vue"),
        meta: {
            layout: "full",
            middleware: [
                guest,
            ]
        },
    },

    {
        path: "/register",
        name: "register",
        component: () => import("@/views/pages/auth/Register.vue"),
        meta: {
            layout: "full",
            guest: true,
            middleware: [
                storeRegistrationType,
                guest,
            ]
        }
    },

    {
        path: "/forgot-password",
        name: "forgot-password",
        component: () => import("@/views/pages/auth/ForgotPassword.vue"),
        meta: {
            layout: "full",
            middleware: [
                guest,
            ]
        },
    },

    // email changed and user setting it's password
    {
        path: "/set-password",
        name: "set-password",
        component: () => import("@/views/pages/auth/ResetPassword.vue"),
        meta: {
            layout: "full",
            middleware: [
                guest,
            ]
        },
    },
    // traditional way of wanting to forgot password
    {
        path: "/reset-password",
        name: "reset-password",
        component: () => import("@/views/pages/auth/ResetPassword.vue"),
        meta: {
            layout: "full",
            middleware: [
                guest,
            ]
        },
    },
    // user finished signing and now he wants to create account just by setting a password
    {
        path: "/create-account",
        name: "create-account-just-by-setting-password",
        component: () => import("@/views/pages/auth/ResetPassword.vue"),
        meta: {
            layout: "full",
            middleware: [
                guest,
            ]
        },
    },
    {
        path: "/token-expire",
        name: "expired-token",
        component: () => import("@/views/pages/auth/TokenExpired.vue"),
        meta: {
            layout: "full",
            middleware: [
                guest,
                tokenExpiry,
            ]
        },
    },
    {
        path: "/verify-email",
        name: "verifyEmail",
        component: () => import("@/views/pages/auth/EmailVerify.vue"),
        meta: {
            layout: "full",
            middleware: [
                auth,
            ]
        }
    },

    {
        path: "/error-404",
        name: "error-404",
        component: () => import("@/views/error/Error404.vue"),
        meta: {
            layout: "full",
        },
    },


    {
        path: "*",
        redirect: "error-404",
    },
];

export default list;
